import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import { distanceKmFromUser as distanceKmFromUserUtil } from '../components/guest-map/distance';
import { NEARBY_RADIUS_KM } from '../components/guest-map/guestMapConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Owns EVERYTHING venue-data for the Guest Map:
 *   - initial fetch + loading flag
 *   - live-patch on Socket.IO `venue_review_changed` broadcasts
 *   - viewport-bounds + tag + category + saved-only + pinned filtering
 *   - `sortedVenues` (list output) + `featuredVenues` ("Closest" carousel)
 *   - bound-change handler + `boundsRef` for the map instance to feed
 *
 * Guarantees the "no re-render storm on pan" property from the
 * original page: `visibleVenues` state is only committed when the set
 * of matched IDs actually changes.
 */
export function useGuestMapVenueData({
  selectedVenueTags,
  selectedCategory,
  pinnedVenueId,
  savedOnly,
  favoriteIds,
  userPosition,
  locationStatus,
  isExpandedRef,
}) {
  const [allVenues, setAllVenues] = useState([]);
  const [visibleVenues, setVisibleVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const boundsRef = useRef(null);
  // Random seed regenerated once per map mount → randomizes "All around you".
  const sessionSeedRef = useRef(Math.random());

  // Haversine distance helper, bound to the current userPosition.
  const distanceKmFromUser = useCallback(
    (venue) => distanceKmFromUserUtil(venue, userPosition),
    [userPosition],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/venues`);
        if (cancelled) return;
        const venues = res.data.filter((v) => v.latitude && v.longitude);
        setAllVenues(venues);
        setVisibleVenues(venues);
      } catch (err) {
        // Swallow: an empty state still renders the location prompt.
        console.error('Error fetching venues:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live patch venue rating when ANY guest submits a new review. Backend
  // broadcasts `venue_review_changed` over Socket.IO; RealtimeProvider
  // re-emits as a window CustomEvent so we stay transport-decoupled.
  useEffect(() => {
    const onReviewChanged = (event) => {
      const detail = event?.detail || {};
      const venueId = detail.venue_id;
      if (!venueId) return;
      const nextRating = typeof detail.rating === 'number' ? detail.rating : null;
      const nextCount = typeof detail.review_count === 'number' ? detail.review_count : null;
      const patchVenue = (v) => (v.id === venueId
        ? {
          ...v,
          ...(nextRating !== null ? { rating: nextRating } : {}),
          ...(nextCount !== null ? { review_count: nextCount } : {}),
        }
        : v);
      setAllVenues((prev) => prev.map(patchVenue));
      setVisibleVenues((prev) => prev.map(patchVenue));
    };
    window.addEventListener('allure:venue-review-changed', onReviewChanged);
    return () => window.removeEventListener('allure:venue-review-changed', onReviewChanged);
  }, []);

  const filterVenues = useCallback((bounds) => {
    // Location gate: venues + experiences are only surfaced when the
    // guest has granted live geolocation. Without it we render empty.
    if (locationStatus !== 'live' || !userPosition) {
      setVisibleVenues((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    let filtered = allVenues;
    // Skip the map-viewport bounds filter when "Saved" is on so the guest
    // can see every favourited venue regardless of where the map is panned.
    if (bounds && !savedOnly) {
      filtered = filtered.filter((v) => (
        v.latitude >= bounds.south && v.latitude <= bounds.north
        && v.longitude >= bounds.west && v.longitude <= bounds.east
      ));
    }
    if (savedOnly) {
      filtered = filtered.filter((v) => favoriteIds.has(v.id));
    }
    if (selectedVenueTags.length > 0) {
      // Multiple tags OR-combined so "sushi" + "pizza" widens the list.
      const wanted = selectedVenueTags.map((t) => (t || '').trim().toLowerCase());
      filtered = filtered.filter((v) => (
        Array.isArray(v.tags) && v.tags.some((tag) => (
          wanted.includes((tag || '').trim().toLowerCase())
        ))
      ));
    }
    if (selectedCategory) {
      filtered = filtered.filter((v) => (
        Array.isArray(v.categories) && v.categories.includes(selectedCategory)
      ));
    }
    // Always keep a pinned venue in the list (map-popup tap, deep link).
    if (pinnedVenueId && !filtered.some((v) => v.id === pinnedVenueId)) {
      const pinned = allVenues.find((v) => v.id === pinnedVenueId);
      if (pinned) filtered = [pinned, ...filtered];
    }
    // Only commit when the resulting ID set changes — prevents a
    // re-render storm on every pan when the visible set stays the same.
    setVisibleVenues((prev) => {
      if (prev.length === filtered.length
        && prev.every((p, i) => p.id === filtered[i].id)) {
        return prev;
      }
      return filtered;
    });
  }, [
    allVenues, selectedVenueTags, selectedCategory, pinnedVenueId,
    savedOnly, favoriteIds, locationStatus, userPosition,
  ]);

  // Re-filter when the filter inputs change (bounds stay stable).
  useEffect(() => {
    filterVenues(boundsRef.current);
  }, [
    selectedVenueTags, selectedCategory, pinnedVenueId,
    savedOnly, favoriteIds, filterVenues,
  ]);

  const handleBoundsChange = useCallback((bounds) => {
    // Only update the venue list bounds while the map is expanded. When
    // shrunk (minimize button OR scroll-compact), keep the last expanded
    // bounds so the list stays "settled" and doesn't drop visible venues.
    if (!isExpandedRef.current) return;
    boundsRef.current = bounds;
    filterVenues(bounds);
  }, [filterVenues, isExpandedRef]);

  // The list filters to the 20 km radius. Exceptions: (1) a pinned venue
  // stays regardless, (2) "Saved" mode surfaces every liked venue.
  const sortedVenues = useMemo(() => {
    let list = [...visibleVenues];
    if (userPosition) {
      list = list.filter((v) => {
        if (distanceKmFromUser(v) <= NEARBY_RADIUS_KM) return true;
        if (pinnedVenueId && v.id === pinnedVenueId) return true;
        if (savedOnly && favoriteIds.has(v.id)) return true;
        return false;
      });
    }
    // Deterministic shuffle by per-session seed so the order feels fresh
    // each session but stays stable while panning/scrolling.
    const seed = sessionSeedRef.current;
    list.sort((a, b) => {
      if (pinnedVenueId) {
        if (a.id === pinnedVenueId) return -1;
        if (b.id === pinnedVenueId) return 1;
      }
      const ha = (((a.id || a.name || '').length + seed * 9301) % 233280);
      const hb = (((b.id || b.name || '').length + seed * 9301) % 233280);
      return ((seed + ha) % 1) - ((seed + hb) % 1) || (a.id || '').localeCompare(b.id || '');
    });
    return list;
  }, [visibleVenues, userPosition, distanceKmFromUser, pinnedVenueId, savedOnly, favoriteIds]);

  // Sourced from `allVenues` (not `visibleVenues`) so it ignores the
  // current map viewport — the guest should always see the ten closest
  // options even when zoomed tight on their own location.
  const featuredVenues = useMemo(() => {
    if (locationStatus !== 'live' || !userPosition) return [];
    const list = [...allVenues];
    return list
      .filter((v) => distanceKmFromUser(v) <= NEARBY_RADIUS_KM)
      .sort((a, b) => distanceKmFromUser(a) - distanceKmFromUser(b))
      .slice(0, 10);
  }, [allVenues, userPosition, distanceKmFromUser, locationStatus]);

  return {
    allVenues,
    setAllVenues,
    visibleVenues,
    sortedVenues,
    featuredVenues,
    loading,
    boundsRef,
    handleBoundsChange,
    distanceKmFromUser,
  };
}
